import { safeLocalStorage } from '@/lib/safeStorage';
import { Buildings } from '@phosphor-icons/react';
import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { Briefcase, FileText, CircleNotch, MapTrifold, MagnifyingGlass, Users, CaretRight, Phone, Envelope, MapPin, Sparkle, ArrowRight, NavigationArrow, CheckCircle } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { useEntityNavigation } from '@/hooks/useEntityNavigation';
import { unifiedSearch, UnifiedSearchResults } from '../../lib/api/routes/search';
import { SidebarMenuButton, useSidebarOptional } from '@/components/ui/sidebar';
import { Popover, PopoverContent, PopoverTrigger, PopoverAnchor } from '../ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Command as CommandPrimitive } from 'cmdk';
import {
  searchCustomers as searchCustomersAPI,
  searchSubscriptions as searchSubscriptionsAPI,
  isSearchAvailable
} from '../../lib/search-api';
import { useAuthStatus } from '../../hooks/useAuthStatus';
import { formatPhoneDisplay, CustomerStatus, resolveCadenceLabel, customerAccountTypeLabel } from '@ruta/shared';
import { reportMapboxApiCall } from '@/lib/integrationUsage';
import { CustomerSetupIcons, type CustomerPaymentSetup } from '@/components/customers/CustomerSetupIcons';
import { formatCurrency } from '../../lib/utils';
import { formatDistanceToNowStrict } from 'date-fns';
import { useOrgServiceRegionCenter } from '@/hooks/useOrgServiceRegionCenter';
import { applyProximity } from '@/lib/geocodeProximity';
import { rankEqualMatchCustomers } from '@/lib/rankSearchCustomers';
import { prepareAddressResults } from '@/lib/addressSearchResults';
import { emptyUnifiedResults, settledSearchParts } from '@/lib/settledSearchParts';
import { usePermission } from '@/hooks/usePermission';
import { Permission } from '@ruta/shared';
import { AddressResultRow, type AddressResult } from './AddressResultRow';
import { SearchCreateCustomerDialog } from './SearchCreateCustomerDialog';

// Category configuration with vibrant, theme-aligned colors for landscaping
const CATEGORIES = {
  customers: {
    label: 'Customers',
    icon: Users,
    gradient: 'from-orange-400 to-orange-600',
    bg: 'bg-orange-500/10',
    activeBg: 'bg-orange-500',
    activeText: 'text-white',
    text: 'text-orange-600',
    border: 'border-orange-500/20',
  },
  services: {
    label: 'Services',
    icon: Briefcase,
    gradient: 'from-green-400 to-green-600',
    bg: 'bg-green-500/10',
    activeBg: 'bg-green-500',
    activeText: 'text-white',
    text: 'text-green-600',
    border: 'border-green-500/20',
  },
  templates: {
    label: 'Service Templates',
    icon: FileText,
    gradient: 'from-primary/70 to-primary',
    bg: 'bg-primary/10',
    activeBg: 'bg-primary',
    activeText: 'text-white',
    text: 'text-primary',
    border: 'border-primary/20',
  },
  serviceAreas: {
    label: 'Areas',
    icon: MapTrifold,
    gradient: 'from-stone-400 to-stone-600',
    bg: 'bg-stone-500/10',
    activeBg: 'bg-stone-500',
    activeText: 'text-white',
    text: 'text-stone-600',
    border: 'border-stone-500/20',
  },
  addresses: {
    label: 'Addresses',
    icon: NavigationArrow,
    gradient: 'from-red-400 to-red-600',
    bg: 'bg-red-500/10',
    activeBg: 'bg-red-500',
    activeText: 'text-white',
    text: 'text-red-600',
    border: 'border-red-500/20',
  },
} as const;

type CategoryKey = keyof typeof CATEGORIES;

// Get initials from a name
function getInitials(name: string | undefined | null): string {
  if (!name) return '??';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0 || !parts[0]) return '??';
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Highlight matching text in search results
function HighlightedText({ text, query, isDark }: { text: string | undefined | null; query: string; isDark?: boolean }) {
  if (!text) return null;
  if (!query?.trim()) return <>{text}</>;

  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);

  return (
    <>
      {parts.map((part, i) => (
        regex.test(part) ? (
          <span key={i} className={cn(
            "font-black",
            isDark ? "text-orange-400" : "text-orange-600"
          )}>{part}</span>
        ) : (
          <span key={i}>{part}</span>
        )
      ))}
    </>
  );
}

// Avatar component with gradient initials
function Avatar({ name, gradient, size = 'md' }: { name: string | undefined | null; gradient: string; size?: 'sm' | 'md' | 'lg' }) {
  const sizeClasses = {
    sm: 'h-8 w-8 text-xs',
    md: 'h-10 w-10 text-sm',
    lg: 'h-12 w-12 text-base',
  };

  return (
    <div className={cn(
      'shrink-0 rounded-xl bg-gradient-to-br font-bold text-white flex items-center justify-center shadow-sm',
      gradient,
      sizeClasses[size]
    )}>
      {getInitials(name)}
    </div>
  );
}

// Status badge component
function StatusBadge({ status }: { status: string }) {
  const statusConfig: Record<string, { bg: string; text: string; dot: string; label?: string }> = {
    [CustomerStatus.ACTIVE.toLowerCase()]: { bg: 'bg-green-100', text: 'text-green-700', dot: 'bg-green-500', label: 'Active' },
    [CustomerStatus.INACTIVE.toLowerCase()]: { bg: 'bg-stone-100', text: 'text-stone-600', dot: 'bg-stone-400', label: 'Inactive' },
    [CustomerStatus.PENDING.toLowerCase()]: { bg: 'bg-orange-100', text: 'text-orange-700', dot: 'bg-orange-500', label: 'Pending' },
    [CustomerStatus.SUSPENDED.toLowerCase()]: { bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-500', label: 'Suspended' },
    [CustomerStatus.PROSPECT.toLowerCase()]: { bg: 'bg-stone-100', text: 'text-stone-700', dot: 'bg-stone-500', label: 'Prospect' },
    [CustomerStatus.CONTACTED.toLowerCase()]: { bg: 'bg-primary/10', text: 'text-primary', dot: 'bg-primary', label: 'Contacted' },
    [CustomerStatus.QUOTED.toLowerCase()]: { bg: 'bg-orange-100', text: 'text-orange-700', dot: 'bg-orange-500', label: 'Quoted' },
    [CustomerStatus.OUT_OF_SERVICE_AREA.toLowerCase()]: { bg: 'bg-orange-100', text: 'text-orange-700', dot: 'bg-orange-500', label: 'Out of Service Area' },
  };

  const normalizedStatus = status.toLowerCase();
  const config = statusConfig[normalizedStatus] || { 
    bg: 'bg-stone-100', 
    text: 'text-stone-600', 
    dot: 'bg-stone-400',
    label: status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  };

  return (
    <span className={cn('inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider whitespace-nowrap', config.bg, config.text)}>
      <span className={cn('w-1.5 h-1.5 rounded-full', config.dot)} />
      {config.label || status}
    </span>
  );
}

interface GlobalSearchProps {
  className?: string;
  variant?: 'default' | 'compact' | 'embedded' | 'panel';
  /** Custom handler for customer selection. If provided, prevents default navigation. */
  onCustomerSelect?: (customer: any) => void;
  /**
   * Alignment of the results popover for the embedded variant. Use 'end' when the
   * input sits at the right edge (e.g. the header utility cluster) so the 540px
   * panel opens leftward instead of overflowing off-screen. Defaults to 'start'.
   */
  popoverAlign?: 'start' | 'end';
  /**
   * Fires after the user picks a result and navigates (or after a custom
   * `onCustomerSelect`). Used by the mobile header panel to close itself.
   */
  onNavigate?: () => void;
}

// Address suggestion from Mapbox, tagged with any customer already there (RUT-4447).
type AddressSuggestion = AddressResult;

// Extended search results that include addresses
interface ExtendedSearchResults extends UnifiedSearchResults {
  results: UnifiedSearchResults['results'] & {
    addresses: AddressSuggestion[];
  };
  count: UnifiedSearchResults['count'] & {
    addresses: number;
  };
}

// View options storage key (must match Map.tsx)
const VIEW_OPTIONS_STORAGE_KEY = 'ruta-map-view-options';

export function GlobalSearch({
  className,
  variant = 'default',
  onCustomerSelect,
  popoverAlign = 'start',
  onNavigate,
}: GlobalSearchProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<ExtendedSearchResults | null>(null);
  const [activeFilter, setActiveFilter] = useState<CategoryKey | 'all'>('all');
  const navigate = useNavigate();
  const { navigateToEntity } = useEntityNavigation();
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const suppressAutoOpenRef = useRef(false);
  const { orgId } = useAuthStatus();
  const useAPISearch = isSearchAvailable();
  // Optional — GlobalSearch can mount in the mobile tools panel; still close
  // the nav sheet when present (RUTA-WEBAPP-1H).
  const setOpenMobile = useSidebarOptional()?.setOpenMobile;
  // Bias address suggestions to the org's service region so a street typed
  // without a city ranks locally instead of returning out-of-state (RUT-3034).
  const proximityCenter = useOrgServiceRegionCenter();
  const canCreateCustomer = usePermission(Permission.CUSTOMERS_MANAGE);
  const [createAtAddress, setCreateAtAddress] = useState<AddressSuggestion | null>(null);

  const MAPBOX_ACCESS_TOKEN = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;

  // Keyboard shortcut to focus search (Cmd/Ctrl + K)
  // Only handles if this component instance is visible
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        // Check if this component is visible before handling
        const container = containerRef.current;
        if (!container || container.offsetParent === null) {
          return; // Component is hidden, don't handle
        }

        e.preventDefault();
        if (variant === 'compact') {
          setOpen(true);
        } else if (variant === 'panel') {
          inputRef.current?.focus();
        } else {
          inputRef.current?.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [variant]);

  // Close popover and suppress auto-open when window loses focus
  useEffect(() => {
    const handleWindowBlur = () => {
      setOpen(false);
      suppressAutoOpenRef.current = true;
    };

    const handleWindowFocus = () => {
      // Reset suppress flag after a short delay to allow normal interaction
      setTimeout(() => {
        suppressAutoOpenRef.current = false;
      }, 100);
    };

    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('focus', handleWindowFocus);
    return () => {
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, []);

  // Parse Mapbox place_name into street and city/state/zip parts
  // Mapbox format: "123 Main St, Minneapolis, Minnesota 55401, United States"
  const parseMapboxAddress = (placeName: string): { streetAddress: string; cityStateZip: string } => {
    // Remove ", United States" from the end
    const withoutCountry = placeName.replace(/,?\s*United States$/i, '');

    // Split by comma - first part is street, rest is city/state/zip
    const parts = withoutCountry.split(',').map(p => p.trim());

    if (parts.length >= 2) {
      return {
        streetAddress: parts[0],
        cityStateZip: parts.slice(1).join(', '),
      };
    }

    return {
      streetAddress: withoutCountry,
      cityStateZip: '',
    };
  };

  // Search Mapbox for address suggestions
  const searchAddresses = async (searchQuery: string): Promise<AddressSuggestion[]> => {
    if (!MAPBOX_ACCESS_TOKEN || searchQuery.length < 3) {
      return [];
    }

    try {
      const params = new URLSearchParams({
        access_token: MAPBOX_ACCESS_TOKEN,
        types: 'address',
        limit: '5',
        country: 'us',
      });
      applyProximity(params, proximityCenter);
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(searchQuery)}.json?${params.toString()}`;
      const response = await fetch(url);

      if (response.ok) {
        void reportMapboxApiCall();
        const data = await response.json();
        return (data.features || []).map((feature: any) => {
          const placeName = feature.place_name || 'Unknown address';
          const { streetAddress, cityStateZip } = parseMapboxAddress(placeName);
          return {
            id: feature.id || Math.random().toString(),
            place_name: placeName,
            center: feature.center || [0, 0],
            streetAddress,
            cityStateZip,
          };
        });
      }
    } catch (error) {
      console.error('Address search error:', error);
    }
    return [];
  };

  // Keep every address, tag the ones a customer already lives at, and rank the
  // exact house first, then nearest the service region. These used to be
  // DROPPED when they matched a customer, which removed the very house the
  // office typed (RUT-4447).
  const filterAddressesAgainstCustomers = (
    addresses: AddressSuggestion[],
    customers: any[]
  ): AddressSuggestion[] => prepareAddressResults(addresses, customers, query, proximityCenter);

  // Perform unified search
  const performSearch = async (searchQuery: string) => {
    if (!searchQuery.trim() || searchQuery.length < 2) {
      setSearchResults(null);
      return;
    }

    setLoading(true);

    try {
      // Use API proxy search if available for faster results
      if (useAPISearch && orgId) {
        // RUT-4598: allSettled, so one failed call (e.g. the templates/areas
        // quick search answering 422) can't blank the whole bar. Show
        // whatever came back; log the rest.
        const [customersRes, subscriptionsRes, backendRes, addressesRes] = await Promise.allSettled([
          searchCustomersAPI({ q: searchQuery, per_page: 10 }),
          searchSubscriptionsAPI({ q: searchQuery, per_page: 10 }),
          unifiedSearch(searchQuery, ['templates', 'serviceAreas']),
          activeFilter === 'addresses' ? searchAddresses(searchQuery) : Promise.resolve([] as AddressSuggestion[]),
        ]);
        const settled = settledSearchParts(
          { customersRes, subscriptionsRes, backendRes, addressesRes },
          (label, reason) => console.error(`Search error (${label}):`, reason),
        );
        if (!settled) {
          setSearchResults(null);
          return;
        }
        const { customerHits, subscriptionHits, templates, serviceAreas, addressResults } = settled;

        // Filter out addresses that match existing customers
        const filteredAddresses = filterAddressesAgainstCustomers(addressResults, customerHits);

        // Combine results: API customers/subscriptions + backend templates/areas + addresses
        setSearchResults({
          results: {
            customers: rankEqualMatchCustomers(customerHits),
            services: subscriptionHits,
            templates,
            serviceAreas,
            addresses: filteredAddresses,
          },
          count: {
            customers: customerHits.length,
            services: subscriptionHits.length,
            templates: templates.length,
            serviceAreas: serviceAreas.length,
            addresses: filteredAddresses.length,
          },
          query: searchQuery,
        });
      } else {
        // Fallback to backend unified search + addresses
        const [unifiedRes, addressesRes] = await Promise.allSettled([
          unifiedSearch(searchQuery),
          activeFilter === 'addresses' ? searchAddresses(searchQuery) : Promise.resolve([] as AddressSuggestion[]),
        ]);
        if (unifiedRes.status === 'rejected') console.error('Search error (unified):', unifiedRes.reason);
        if (addressesRes.status === 'rejected') console.error('Search error (addresses):', addressesRes.reason);
        if (unifiedRes.status === 'rejected' && addressesRes.status === 'rejected') {
          setSearchResults(null);
          return;
        }
        const results = unifiedRes.status === 'fulfilled' ? unifiedRes.value : emptyUnifiedResults(searchQuery);
        const addressResults = addressesRes.status === 'fulfilled' ? addressesRes.value : [];

        // Filter out addresses that match existing customers
        const filteredAddresses = filterAddressesAgainstCustomers(addressResults, results.results.customers);

        setSearchResults({
          ...results,
          results: {
            ...results.results,
            customers: rankEqualMatchCustomers(results.results.customers),
            addresses: filteredAddresses,
          },
          count: {
            ...results.count,
            addresses: filteredAddresses.length,
          },
        } as ExtendedSearchResults);
      }
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Open/close popover based on query for default and embedded variants
    // (compact variant uses its own trigger mechanism)
    if (variant !== 'compact') {
      if (query.length >= 2) {
        // Don't auto-open if suppressed (e.g., after window blur)
        if (!suppressAutoOpenRef.current) {
          setOpen(true);
        }
        setLoading(true);
      } else {
        setOpen(false);
        setLoading(false);
        setSearchResults(null);
      }
    }

    const debounceTimer = setTimeout(() => {
      if (query.length >= 2) {
        performSearch(query);
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [query, variant]);

  // Fetch addresses when the Addresses tab is activated
  useEffect(() => {
    if (activeFilter === 'addresses' && query.length >= 2 && !searchResults?.results.addresses?.length) {
      setLoading(true);
      searchAddresses(query).then(addressResults => {
        setSearchResults(prev => {
          if (!prev) return prev;
          const filteredAddresses = filterAddressesAgainstCustomers(addressResults, prev.results.customers);
          return {
            ...prev,
            results: { ...prev.results, addresses: filteredAddresses },
            count: { ...prev.count, addresses: filteredAddresses.length },
          };
        });
        setLoading(false);
      }).catch(() => setLoading(false));
    }
  }, [activeFilter]);

  const handleSelect = (callback: () => void) => {
    callback();
    setOpen(false);
    setQuery('');
    setSearchResults(null);
    // Close mobile sidebar to prevent two Sheets being open simultaneously
    setOpenMobile?.(false);
    onNavigate?.();
  };

  const handleCustomerClick = (customerId: string, customer?: any) => {
    if (onCustomerSelect && customer) {
      handleSelect(() => onCustomerSelect(customer));
    } else {
      // Default row action: open the customer's full details page.
      handleSelect(() => navigateToEntity('CUSTOMER', { id: customerId }));
    }
  };

  // Explicit "open on map" action for a customer search result.
  const handleCustomerMapClick = (customerId: string) => {
    handleSelect(() => navigate(`/map?view=customers&customerId=${customerId}`));
  };

  const handleServiceClick = (serviceTemplateId: string, customerId: string) => {
    handleSelect(() => navigate(`/map?view=customers&customerId=${customerId}&serviceTemplateId=${serviceTemplateId}`));
  };

  const handleTemplateClick = (templateId: string) => {
    handleSelect(() => navigateToEntity('SERVICE_TEMPLATE', { id: templateId }));
  };

  const handleAreaClick = (areaId: string) => {
    handleSelect(() => navigate(`/service-area?highlight=${areaId}`));
  };

  const handleAddressClick = (address: AddressSuggestion) => {
    handleSelect(() => {
      // Enable viewport parcels in localStorage
      try {
        const stored = safeLocalStorage.getItem(VIEW_OPTIONS_STORAGE_KEY);
        const viewOptions = stored ? JSON.parse(stored) : {};
        viewOptions.showViewportParcels = true;
        safeLocalStorage.setItem(VIEW_OPTIONS_STORAGE_KEY, JSON.stringify(viewOptions));
      } catch (e) {
        // Ignore storage errors
      }

      // Navigate to map with parcel coordinates
      const [lng, lat] = address.center;
      navigate(`/map?view=customers&parcelLng=${lng}&parcelLat=${lat}`);
    });
  };

  const totalResults = searchResults ?
    searchResults.count.customers + searchResults.count.services +
    searchResults.count.templates + searchResults.count.serviceAreas +
    (searchResults.count.addresses || 0) : 0;

  // Get counts for filter tabs
  const categoryCounts = useMemo(() => {
    if (!searchResults) return { customers: 0, services: 0, templates: 0, serviceAreas: 0, addresses: 0 };
    return {
      ...searchResults.count,
      addresses: searchResults.count.addresses || 0,
    };
  }, [searchResults]);

  // Filter results based on active filter
  const filteredResults = useMemo(() => {
    if (!searchResults) return null;
    if (activeFilter === 'all') return searchResults;

    return {
      ...searchResults,
      results: {
        customers: activeFilter === 'customers' ? searchResults.results.customers : [],
        services: activeFilter === 'services' ? searchResults.results.services : [],
        templates: activeFilter === 'templates' ? searchResults.results.templates : [],
        serviceAreas: activeFilter === 'serviceAreas' ? searchResults.results.serviceAreas : [],
        addresses: activeFilter === 'addresses' ? (searchResults.results.addresses || []) : [],
      },
      count: {
        customers: activeFilter === 'customers' ? searchResults.count.customers : 0,
        services: activeFilter === 'services' ? searchResults.count.services : 0,
        templates: activeFilter === 'templates' ? searchResults.count.templates : 0,
        serviceAreas: activeFilter === 'serviceAreas' ? searchResults.count.serviceAreas : 0,
        addresses: activeFilter === 'addresses' ? (searchResults.count.addresses || 0) : 0,
      },
    };
  }, [searchResults, activeFilter]);

  // Category filter tabs component
  const CategoryTabs = ({ isDark }: { isDark?: boolean }) => {
    const categories: (CategoryKey | 'all')[] = ['all', 'customers', 'services', 'templates', 'serviceAreas', 'addresses'];

    return (
      <div className={cn(
        "flex items-center gap-1 px-4 py-3 border-b overflow-x-auto no-scrollbar",
        isDark ? "border-white/10 bg-nav" : "border-stone-200 bg-white"
      )}>
        {categories.map((cat) => {
          const isAll = cat === 'all';
          const count = isAll ? totalResults : categoryCounts[cat as CategoryKey];
          const config = isAll ? null : CATEGORIES[cat as CategoryKey];
          const Icon = config?.icon;
          const isActive = activeFilter === cat;

          if (!isAll && count === 0 && cat !== 'addresses') return null;

          return (
            <button
              key={cat}
              onClick={() => setActiveFilter(cat)}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 whitespace-nowrap',
                isActive
                  ? isAll
                    ? isDark ? 'bg-white text-stone-900 shadow-lg scale-105' : 'bg-stone-800 text-white shadow-lg scale-105'
                    : cn('shadow-lg scale-105', config?.activeBg, config?.activeText)
                  : isDark
                    ? 'text-white/70 hover:text-white hover:bg-white/20'
                    : 'text-stone-500 hover:text-stone-800 hover:bg-stone-100'
              )}
            >
              {isAll ? (
                <MagnifyingGlass className="w-3 h-3" strokeWidth={3} />
              ) : (
                Icon && <Icon className="w-3 h-3" strokeWidth={3} />
              )}
              {isAll ? 'All' : config?.label}
              <span className={cn(
                'ml-0.5 text-[10px] font-black tabular-nums',
                isActive ? 'opacity-100' : 'opacity-40'
              )}>
                {count}
              </span>
            </button>
          );
        })}
      </div>
    );
  };

  // Result card component for customers
  const CustomerCard = ({ customer, isDark }: { customer: any; isDark?: boolean }) => {
    const cat = CATEGORIES.customers;
    const customerName = customer.name ||
      [customer.firstName, customer.lastName].filter(Boolean).join(' ') ||
      'Unknown Customer';

    // Find email/phone hits in arrays
    const emailHits = (customer.emails || []).filter((e: string) =>
      query.length >= 2 && e.toLowerCase().includes(query.toLowerCase()) && e !== customer.email
    );
    const phoneHits = (customer.phones || []).filter((p: string) =>
      query.length >= 2 && p.includes(query) && p !== customer.phone
    );

    const hasHits = emailHits.length > 0 || phoneHits.length > 0;
    const hasContact = customer.email || customer.phone;

    return (
      <CommandItem
        value={`customer-${customer.id}-${customerName}`}
        onSelect={() => handleCustomerClick(customer.id, customer)}
        className={cn(
          'w-full group flex items-start gap-3 p-3 rounded-xl transition-all duration-200',
          'cursor-pointer text-left border mb-2 last:mb-0 shadow-sm',
          isDark
            ? 'bg-white/5 border-white/10 data-[selected=true]:bg-white/20 data-[selected=true]:border-white/30'
            : 'bg-white border-stone-200 data-[selected=true]:border-primary data-[selected=true]:ring-1 data-[selected=true]:ring-primary/10'
        )}
      >
        <Avatar name={customerName} gradient={cat.gradient} size="md" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-0.5">
            <span className={cn(
              "font-bold text-sm truncate",
              isDark ? "text-white" : "text-stone-900"
            )}>
              <HighlightedText text={customerName} query={query} isDark={isDark} />
            </span>
            <div className="flex items-center gap-2 shrink-0">
              <CustomerSetupIcons
                iconClassName="h-3.5 w-3.5"
                payment={(customer.paymentMethodStatus as CustomerPaymentSetup) || 'none'}
                termsAccepted={!!customer.hasAcceptedTerms}
                hasPrimaryContact={!!customer.hasPrimaryContact}
              />
              {customer.status && (
                <StatusBadge status={customer.status} />
              )}
            </div>
          </div>

          {hasContact && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-1">
              {customer.email && (
                <span className={cn(
                  "inline-flex items-center gap-1 text-xs truncate",
                  isDark ? "text-white/60" : "text-stone-500 font-medium"
                )}>
                  <Envelope className="w-3.5 h-3.5 shrink-0 opacity-60" />
                  <HighlightedText text={customer.email} query={query} isDark={isDark} />
                </span>
              )}
              {customer.phone && (
                <span className={cn(
                  "inline-flex items-center gap-1 text-xs",
                  isDark ? "text-white/60" : "text-stone-500 font-medium"
                )}>
                  <Phone className="w-3.5 h-3.5 shrink-0 opacity-60" />
                  <HighlightedText text={formatPhoneDisplay(customer.phone)} query={query} isDark={isDark} />
                </span>
              )}
            </div>
          )}

          {/* Email/Phone Hits */}
          {hasHits && (
            <div className={cn(
              "flex flex-wrap items-center gap-x-3 gap-y-1 pt-1.5 mt-1.5",
              isDark ? "border-t border-white/10" : "border-t border-stone-100"
            )}>
              {emailHits.map((email: string, idx: number) => (
                <span key={`email-hit-${idx}`} className={cn(
                  "inline-flex items-center gap-1 text-[10px] truncate italic font-medium",
                  isDark ? "text-white/40" : "text-stone-400"
                )}>
                  <Envelope className="w-3 h-3 shrink-0 opacity-40" />
                  <HighlightedText text={email} query={query} isDark={isDark} />
                </span>
              ))}
              {phoneHits.map((phone: string, idx: number) => (
                <span key={`phone-hit-${idx}`} className={cn(
                  "inline-flex items-center gap-1 text-[10px] italic font-medium",
                  isDark ? "text-white/40" : "text-stone-400"
                )}>
                  <Phone className="w-3 h-3 shrink-0 opacity-40" />
                  <HighlightedText text={formatPhoneDisplay(phone)} query={query} isDark={isDark} />
                </span>
              ))}
            </div>
          )}

          {customer.address && (
            <div className={cn(
              "flex items-center gap-1 text-[10px] mt-1 opacity-80",
              isDark ? "text-white/40" : "text-stone-400"
            )}>
              <MapPin className="w-3 h-3 shrink-0 opacity-40" />
              <span className="truncate">{customer.address}</span>
            </div>
          )}

          {customer.account && (
            <div className={cn('mt-1 flex items-center gap-1 text-xs', isDark ? 'text-white/70' : 'text-stone-500')}>
              <Buildings className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{customer.account.name} · {customerAccountTypeLabel(customer.account)}</span>
            </div>
          )}

          {/* Footer row: balance/portal meta on the left, the "View on map"
              action on the right, sharing one baseline so the action stays in
              line with the card content instead of floating on its own row. */}
          {(!onCustomerSelect || customer.balance != null || customer.lastPortalActiveAt) && (
            <div className="flex items-center justify-between gap-3 mt-1.5">
              <div className="flex items-center gap-3 min-w-0">
                {customer.balance != null && customer.balance !== 0 && (
                  <span className={cn(
                    "text-[10px] font-semibold tabular-nums whitespace-nowrap",
                    customer.balance > 0
                      ? isDark ? "text-red-400" : "text-red-600"
                      : isDark ? "text-green-400" : "text-green-600"
                  )}>
                    {formatCurrency(Math.abs(customer.balance))} {customer.balance > 0 ? "due" : "credit"}
                  </span>
                )}
                {customer.lastPortalActiveAt && (
                  <span className={cn(
                    "inline-flex items-center gap-1 text-[10px] font-medium whitespace-nowrap",
                    isDark ? "text-green-400" : "text-green-600"
                  )}>
                    <CheckCircle className="w-3 h-3" />
                    Portal: {formatDistanceToNowStrict(new Date(customer.lastPortalActiveAt), { addSuffix: true })}
                  </span>
                )}
              </div>
              {/* Neutral (non-status) "View on map" action. Green and red are
                  already spoken for on this card (active/portal, balance), so a
                  quiet stone link + hover chip reads as a utility action without
                  competing. stopPropagation keeps it from also opening details. */}
              {!onCustomerSelect && (
                <button
                  type="button"
                  aria-label="View on map"
                  title="View on map"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCustomerMapClick(customer.id);
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                  className={cn(
                    "inline-flex items-center gap-1 shrink-0 -mr-1.5 px-1.5 py-0.5 rounded-md text-[11px] font-medium transition-colors",
                    "focus-visible:outline-none focus-visible:ring-1",
                    isDark
                      ? "text-white/40 hover:text-white hover:bg-white/10 focus-visible:ring-white/30 group-data-[selected=true]:text-white/70"
                      : "text-stone-400 hover:text-stone-700 hover:bg-stone-100 focus-visible:ring-stone-300 group-data-[selected=true]:text-stone-600"
                  )}
                >
                  <MapTrifold className="w-3.5 h-3.5" />
                  View on map
                </button>
              )}
            </div>
          )}
        </div>
        {onCustomerSelect && (
          <div className="flex items-center self-center">
            <CaretRight className={cn(
              "w-5 h-5 transition-all",
              isDark ? "text-white/20 group-data-[selected=true]:text-white" : "text-stone-500 group-data-[selected=true]:text-primary",
              "group-data-[selected=true]:translate-x-1"
            )} strokeWidth={3} />
          </div>
        )}
      </CommandItem>
    );
  };

  // Result card for services
  const ServiceCard = ({ service, isDark }: { service: any; isDark?: boolean }) => {
    const cat = CATEGORIES.services;
    const serviceName = service.serviceTemplateName || service.name;
    return (
      <CommandItem
        value={`service-${service.id}-${serviceName}`}
        onSelect={() => handleServiceClick(service.serviceTemplateId, service.customerId)}
        className={cn(
          'w-full group flex items-start gap-3 p-3 rounded-xl transition-all duration-200',
          'cursor-pointer text-left border mb-2 last:mb-0 shadow-sm',
          isDark
            ? 'bg-white/5 border-white/10 data-[selected=true]:bg-white/20 data-[selected=true]:border-white/30'
            : 'bg-white border-stone-200 data-[selected=true]:border-primary data-[selected=true]:ring-1 data-[selected=true]:ring-primary/10'
        )}
      >
        <div className={cn('shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br flex items-center justify-center text-white shadow-sm', cat.gradient)}>
          <Briefcase className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0 space-y-1">
          <div className="flex items-center justify-between gap-2">
            <span className={cn(
              "font-bold text-sm",
              isDark ? "text-white" : "text-stone-900"
            )}>
              <HighlightedText text={serviceName} query={query} isDark={isDark} />
            </span>
            {service.state && (
              <StatusBadge status={service.state} />
            )}
          </div>
          <div className={cn(
            "flex items-center gap-2 text-xs",
            isDark ? "text-white/50" : "text-stone-500"
          )}>
            <span className={cn(
              "inline-flex items-center gap-1",
              isDark ? "text-white/60" : "font-medium text-stone-600"
            )}>
              <Users className="w-3.5 h-3.5 opacity-60" />
              <HighlightedText text={service.customerName} query={query} isDark={isDark} />
            </span>
            {resolveCadenceLabel(service.frequency) && (
              <>
                <span className="opacity-30">•</span>
                <span className="opacity-70">{resolveCadenceLabel(service.frequency)}</span>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center self-center">
          <CaretRight className={cn(
            "w-5 h-5 transition-all",
            isDark ? "text-white/20 group-data-[selected=true]:text-white" : "text-stone-500 group-data-[selected=true]:text-primary",
            "group-data-[selected=true]:translate-x-1"
          )} strokeWidth={3} />
        </div>
      </CommandItem>
    );
  };

  // Result card for templates
  const TemplateCard = ({ template, isDark }: { template: any; isDark?: boolean }) => {
    const cat = CATEGORIES.templates;
    return (
      <CommandItem
        value={`template-${template.id}-${template.name}`}
        onSelect={() => handleTemplateClick(template.id)}
        className={cn(
          'w-full group flex items-start gap-3 p-3 rounded-xl transition-all duration-200',
          'cursor-pointer text-left border mb-2 last:mb-0 shadow-sm',
          isDark
            ? 'bg-white/5 border-white/10 data-[selected=true]:bg-white/20 data-[selected=true]:border-white/30'
            : 'bg-white border-stone-200 data-[selected=true]:border-primary data-[selected=true]:ring-1 data-[selected=true]:ring-primary/10'
        )}
      >
        <div className={cn("shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br flex items-center justify-center text-white shadow-sm", cat.gradient)}>
          <FileText className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0 space-y-1">
          <span className={cn(
            "font-bold text-sm block",
            isDark ? "text-white" : "text-stone-900"
          )}>
            <HighlightedText text={template.name} query={query} isDark={isDark} />
          </span>
          {template.description && (
            <p className={cn(
              "text-[10px] line-clamp-1 font-medium",
              isDark ? "text-white/40" : "text-stone-500"
            )}>
              {template.description}
            </p>
          )}
        </div>
        <div className="flex items-center self-center">
          <CaretRight className={cn(
            "w-5 h-5 transition-all",
            isDark ? "text-white/20 group-data-[selected=true]:text-white" : "text-stone-500 group-data-[selected=true]:text-primary",
            "group-data-[selected=true]:translate-x-1"
          )} strokeWidth={3} />
        </div>
      </CommandItem>
    );
  };

  // Result card for service areas
  const AreaCard = ({ area, isDark }: { area: any; isDark?: boolean }) => {
    const cat = CATEGORIES.serviceAreas;
    return (
      <CommandItem
        value={`area-${area.id}-${area.name}`}
        onSelect={() => handleAreaClick(area.id)}
        className={cn(
          'w-full group flex items-start gap-3 p-3 rounded-xl transition-all duration-200',
          'cursor-pointer text-left border mb-2 last:mb-0 shadow-sm',
          isDark
            ? 'bg-white/5 border-white/10 data-[selected=true]:bg-white/20 data-[selected=true]:border-white/30'
            : 'bg-white border-stone-200 data-[selected=true]:border-primary data-[selected=true]:ring-1 data-[selected=true]:ring-primary/10'
        )}
      >
        <div className={cn("shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br flex items-center justify-center text-white shadow-sm", cat.gradient)}>
          <MapTrifold className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0 space-y-1">
          <span className={cn(
            "font-bold text-sm block",
            isDark ? "text-white" : "text-stone-900"
          )}>
            <HighlightedText text={area.name} query={query} isDark={isDark} />
          </span>
          {area.description && (
            <p className={cn(
              "text-[10px] line-clamp-1 font-medium",
              isDark ? "text-white/40" : "text-stone-500"
            )}>
              {area.description}
            </p>
          )}
        </div>
        <div className="flex items-center self-center">
          <CaretRight className={cn(
            "w-5 h-5 transition-all",
            isDark ? "text-white/20 group-data-[selected=true]:text-white" : "text-stone-500 group-data-[selected=true]:text-primary",
            "group-data-[selected=true]:translate-x-1"
          )} strokeWidth={3} />
        </div>
      </CommandItem>
    );
  };

  // Result card for addresses
  const AddressCard = ({ address, isDark }: { address: AddressSuggestion; isDark?: boolean }) => (
    <AddressResultRow
      address={address}
      isDark={isDark}
      canCreate={canCreateCustomer}
      onOpen={handleAddressClick}
      onCreate={(a) => handleSelect(() => setCreateAtAddress(a))}
    />
  );


  // Section header component
  const SectionHeader = ({ category, isDark }: { category: CategoryKey; isDark?: boolean }) => {
    const cat = CATEGORIES[category];
    const Icon = cat.icon;
    return (
      <div className={cn('flex items-center gap-2 px-3 py-2 mt-4 first:mt-0 mb-1')}>
        <span className={cn(
          'text-[10px] font-black uppercase tracking-[0.2em]',
          isDark ? 'text-white/50' : 'text-stone-400'
        )}>
          {cat.label}
        </span>
        <div className={cn(
          "flex-1 h-px ml-2",
          isDark ? "bg-white/10" : "bg-stone-200"
        )} />
      </div>
    );
  };

  const renderResults = (isDark?: boolean) => (
    <div className="flex flex-col h-full">
      {/* Category filter tabs */}
      {searchResults && totalResults > 0 && <CategoryTabs isDark={isDark} />}

      <div className={cn(
        "flex-1 overflow-y-auto min-h-0",
        isDark ? "bg-white/5" : "bg-stone-50"
      )}>
        {/* Empty state */}
        {!loading && totalResults === 0 && query.length >= 2 && (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <div className={cn(
              "w-16 h-16 rounded-3xl flex items-center justify-center mb-4 border",
              isDark ? "bg-white/5 border-white/10" : "bg-white border-stone-200 shadow-sm"
            )}>
              <MagnifyingGlass className={cn(
                "w-8 h-8",
                isDark ? "text-white/20" : "text-stone-300"
              )} strokeWidth={1.5} />
            </div>
            <p className={cn(
              "text-sm font-semibold mb-1",
              isDark ? "text-white/80" : "text-stone-800"
            )}>No results found</p>
            <p className={cn(
              "text-xs text-center max-w-[200px] leading-relaxed",
              isDark ? "text-white/30" : "text-stone-400"
            )}>
              We couldn't find any matches for "<span className={isDark ? "text-white/50" : "text-stone-600 font-medium"}>{query}</span>".
            </p>
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="relative">
              <div className={cn(
                "w-12 h-12 rounded-2xl border animate-pulse",
                isDark ? "bg-white/5 border-white/10" : "bg-white border-stone-200 shadow-sm"
              )} />
              <div className="absolute inset-0 flex items-center justify-center">
                <CircleNotch className={cn(
                  "w-5 h-5 animate-spin",
                  isDark ? "text-white/40" : "text-stone-400"
                )} />
              </div>
            </div>
            <p className={cn(
              "text-[10px] font-bold uppercase tracking-widest mt-4",
              isDark ? "text-white/30" : "text-stone-400"
            )}>Searching</p>
          </div>
        )}

        {/* Results */}
        {filteredResults && !loading && (
          <div className="p-4 space-y-1">
            {/* Customers */}
            {filteredResults.count.customers > 0 && (
              <CommandGroup className="p-0">
                {activeFilter === 'all' && <SectionHeader category="customers" isDark={isDark} />}
                <div className="space-y-1 mt-1">
                  {filteredResults.results.customers.map((customer) => (
                    <CustomerCard key={customer.id} customer={customer} isDark={isDark} />
                  ))}
                </div>
              </CommandGroup>
            )}

            {/* Services */}
            {filteredResults.count.services > 0 && (
              <CommandGroup className="p-0">
                {activeFilter === 'all' && <SectionHeader category="services" isDark={isDark} />}
                <div className="space-y-1 mt-1">
                  {filteredResults.results.services.map((service) => (
                    <ServiceCard key={service.id} service={service} isDark={isDark} />
                  ))}
                </div>
              </CommandGroup>
            )}

            {/* Templates */}
            {filteredResults.count.templates > 0 && (
              <CommandGroup className="p-0">
                {activeFilter === 'all' && <SectionHeader category="templates" isDark={isDark} />}
                <div className="space-y-1 mt-1">
                  {filteredResults.results.templates.map((template) => (
                    <TemplateCard key={template.id} template={template} isDark={isDark} />
                  ))}
                </div>
              </CommandGroup>
            )}

            {/* Service Areas */}
            {filteredResults.count.serviceAreas > 0 && (
              <CommandGroup className="p-0">
                {activeFilter === 'all' && <SectionHeader category="serviceAreas" isDark={isDark} />}
                <div className="space-y-1 mt-1">
                  {filteredResults.results.serviceAreas.map((area) => (
                    <AreaCard key={area.id} area={area} isDark={isDark} />
                  ))}
                </div>
              </CommandGroup>
            )}

            {/* Addresses */}
            {(filteredResults.count.addresses || 0) > 0 && (
              <CommandGroup className="p-0">
                {activeFilter === 'all' && <SectionHeader category="addresses" isDark={isDark} />}
                <div className="space-y-1 mt-1">
                  {(filteredResults.results.addresses || []).map((address) => (
                    <AddressCard key={address.id} address={address} isDark={isDark} />
                  ))}
                </div>
              </CommandGroup>
            )}
          </div>
        )}
      </div>
    </div>
  );

  // Keyboard hints footer - rendered outside CommandList so always visible
  const KeyboardHints = ({ isDark }: { isDark?: boolean }) => (
    <div className={cn(
      "flex items-center justify-between px-4 py-3 border-t text-[10px]",
      isDark ? "border-white/10 bg-black/40 text-white/60" : "border-stone-100 bg-stone-50/50 text-stone-400"
    )}>
      <div className="flex items-center gap-4">
        <span className="inline-flex items-center gap-1.5">
          <kbd className={cn(
            "px-1.5 py-0.5 rounded border font-mono text-[9px]",
            isDark ? "border-white/10 bg-white/5" : "border-stone-200 bg-white"
          )}>↑↓</kbd>
          Navigate
        </span>
        <span className="inline-flex items-center gap-1.5">
          <kbd className={cn(
            "px-1.5 py-0.5 rounded border font-mono text-[9px]",
            isDark ? "border-white/10 bg-white/5" : "border-stone-200 bg-white"
          )}>↵</kbd>
          Select
        </span>
      </div>
      <span className="font-medium tabular-nums">{totalResults} results</span>
    </div>
  );

  // Panel variant — input + results in document flow (no nested popover).
  // Used by the mobile header tools panel so results don't jump around.
  const createDialog = (
    <SearchCreateCustomerDialog target={createAtAddress} onClose={() => setCreateAtAddress(null)} />
  );

  if (variant === 'panel') {
    return (
      <Command shouldFilter={false} className={cn('flex h-full min-h-0 w-full flex-col border-none bg-white', className)}>
        <div ref={containerRef} className="flex h-full min-h-0 flex-col">
          <div className="flex shrink-0 items-center gap-2 border-b border-stone-200 px-3 py-2.5">
            <MagnifyingGlass className="h-4 w-4 shrink-0 text-stone-400" strokeWidth={2.5} />
            <CommandPrimitive.Input
              ref={inputRef}
              autoFocus
              placeholder="Search customers, services…"
              value={query}
              onValueChange={setQuery}
              className="h-9 min-w-0 flex-1 bg-transparent text-base outline-none placeholder:text-stone-400 text-stone-900"
            />
            {query.length > 0 && (
              <button
                type="button"
                onClick={() => {
                  setQuery('');
                  setSearchResults(null);
                  inputRef.current?.focus();
                }}
                className="shrink-0 rounded-md px-2 py-1 text-xs font-medium text-stone-500 hover:bg-stone-100 hover:text-stone-800"
              >
                Clear
              </button>
            )}
          </div>
          <CommandList className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
            {query.length < 2 ? (
              <div className="px-4 py-10 text-center text-sm text-stone-400">
                Type at least 2 characters to search
              </div>
            ) : (
              renderResults(false)
            )}
          </CommandList>
          {searchResults && totalResults > 0 && <KeyboardHints isDark={false} />}
          {createDialog}
        </div>
      </Command>
    );
  }

  // Compact variant for collapsed sidebar
  if (variant === 'compact') {
    return (
      <div ref={containerRef}>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <SidebarMenuButton tooltip="Search (⌘K)" className={className}>
              <MagnifyingGlass className="h-4 w-4" strokeWidth={1} />
            </SidebarMenuButton>
          </PopoverTrigger>
        <PopoverContent
          side="right"
          align="start"
          className="w-[min(540px,calc(100vw-1rem))] p-0 shadow-2xl z-[9999] overflow-hidden rounded-2xl border-stone-200"
          sideOffset={8}
          collisionPadding={12}
          avoidCollisions={true}
        >
          <Command shouldFilter={false} className="w-full border-none">
            <div className="flex items-center gap-3 px-4 py-3 border-b border-stone-200 bg-white">
              <div className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center">
                <MagnifyingGlass className="h-4 w-4 text-green-600" strokeWidth={2.5} />
              </div>
              <CommandInput
                placeholder="Search customers, services, templates..."
                value={query}
                onValueChange={setQuery}
                className="flex-1 h-8 bg-transparent border-none focus:ring-0 p-0 text-base sm:text-sm font-bold text-stone-900 placeholder:text-stone-300"
              />
            </div>
            <CommandList className="max-h-[520px] overflow-y-auto">
              {renderResults(false)}
            </CommandList>
            {searchResults && totalResults > 0 && <KeyboardHints isDark={false} />}
          </Command>
        </PopoverContent>
        </Popover>
        {createDialog}
      </div>
    );
  }

  // Embedded variant for dark sidebar
  // Command must wrap both input and list for keyboard navigation to work
  if (variant === 'embedded') {
    return (
      <Command shouldFilter={false} className="h-full w-full bg-transparent text-inherit">
        <div ref={containerRef} className="h-full w-full">
          <Popover open={open} onOpenChange={(newOpen) => {
            if (newOpen && query.length < 2) {
              return;
            }
            setOpen(newOpen);
          }}>
            <PopoverAnchor asChild>
              <div className={cn("relative h-full w-full group", className)}>
                <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/70 group-hover:text-white/90 transition-colors pointer-events-none z-10" />
                <CommandPrimitive.Input
                  ref={inputRef}
                  placeholder="Search..."
                  className={cn(
                    // text-base on mobile avoids iOS focus zoom; sm:text-sm matches desktop chrome
                    "pl-9 pr-2 sm:pr-4 h-full w-full bg-black/30 hover:bg-black/40 border border-white/20 hover:border-white/30 outline-none text-base sm:text-sm transition-all focus:border-white/40 focus:ring-1 focus:ring-white/20 placeholder:!text-white/40 !text-white shadow-inner",
                    open ? "rounded-t-lg rounded-b-none border-b-0 bg-black/40 border-white/40" : "rounded-lg"
                  )}
                  value={query}
                  onValueChange={setQuery}
                  onFocus={(e) => {
                    // Only open on focus if this is a user-initiated focus (e.g., clicking the input),
                    // not when regaining window focus. We detect this by checking if the related target
                    // is null (window focus change) vs an element (tab navigation or click).
                    if (query.length >= 2 && e.relatedTarget !== null) {
                      setOpen(true);
                    }
                  }}
                />
                <div className="absolute right-2 top-1/2 -translate-y-1/2 hidden md:flex items-center gap-1 pointer-events-none">
                  <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded bg-white/10 px-1.5 font-mono text-[10px] font-medium text-white/50">
                    <span className="text-xs">⌘</span>K
                  </kbd>
                </div>
              </div>
            </PopoverAnchor>
            <PopoverContent
              align={popoverAlign}
              side="bottom"
              className="w-[min(540px,calc(100vw-1rem))] p-0 shadow-2xl z-[9999] overflow-hidden rounded-b-2xl border border-stone-200 bg-stone-50 text-stone-900"
              sideOffset={0}
              collisionPadding={12}
              avoidCollisions
              onOpenAutoFocus={(e) => {
                e.preventDefault();
                inputRef.current?.focus();
              }}
            >
              <div className="bg-stone-50">
                <CommandList className="max-h-[520px] overflow-y-auto">
                  {renderResults(false)}
                </CommandList>
                {searchResults && totalResults > 0 && <KeyboardHints isDark={false} />}
              </div>
            </PopoverContent>
          </Popover>
          {createDialog}
        </div>
      </Command>
    );
  }

  // Default variant
  // Command must wrap both input and list for keyboard navigation to work
  return (
    <Command shouldFilter={false} className="h-full w-full">
      <div ref={containerRef} className="h-full w-full">
        <Popover open={open} onOpenChange={(newOpen) => {
          // Only allow opening if we have enough characters or it's via keyboard shortcut
          if (newOpen && query.length < 2) {
            // Do nothing, keep closed
            return;
          }
          setOpen(newOpen);
        }}>
          <PopoverAnchor asChild>
            <div className={cn("relative h-full w-full group", className)}>
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors pointer-events-none z-10" />
              <CommandPrimitive.Input
                ref={inputRef}
                placeholder="Search..."
                className="pl-9 pr-4 h-full w-full bg-muted/50 hover:bg-muted border border-transparent hover:border-border rounded-md outline-none text-base sm:text-sm transition-all focus:border-primary/50 focus:ring-2 focus:ring-primary/20 placeholder:text-muted-foreground/70 text-foreground"
                value={query}
                onValueChange={setQuery}
                onFocus={(e) => {
                  // Only open on focus if this is a user-initiated focus (e.g., clicking the input),
                  // not when regaining window focus. We detect this by checking if the related target
                  // is null (window focus change) vs an element (tab navigation or click).
                  if (query.length >= 2 && e.relatedTarget !== null) {
                    setOpen(true);
                  }
                }}
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 hidden md:flex items-center gap-1 pointer-events-none">
                <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100">
                  <span className="text-xs">⌘</span>K
                </kbd>
              </div>
            </div>
          </PopoverAnchor>
          <PopoverContent
            align="start"
            className="w-[min(540px,calc(100vw-1rem))] p-0 shadow-2xl overflow-hidden rounded-2xl border-stone-200"
            collisionPadding={12}
            onOpenAutoFocus={(e) => {
              e.preventDefault();
              inputRef.current?.focus();
            }}
          >
            {/* Search input header - shows current query */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-stone-200 bg-white">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <MagnifyingGlass className="h-4 w-4 text-primary" strokeWidth={2.5} />
              </div>
              <span className="text-base sm:text-sm font-bold text-stone-900 truncate">{query || 'Search…'}</span>
            </div>
            <CommandList className="max-h-[520px] overflow-y-auto">
              {renderResults(false)}
            </CommandList>
            {searchResults && totalResults > 0 && <KeyboardHints isDark={false} />}
          </PopoverContent>
        </Popover>
        {createDialog}
      </div>
    </Command>
  );
}
